import {
  PERCEPTION_MODELS,
  PERCEPTION_MODEL_EXPLORE,
  PERCEPTION_MODEL_JOB,
  PERCEPTION_MODEL_NEW,
  PERCEPTION_MODEL_NORM_SET,
  PERCEPTION_MODEL_TRAIN,
  PERCEPTION_MODEL_TRANSLATION,
  PERCEPTION_MODEL_TRANSLATION_NEW,
} from 'constants/routePaths';

import * as React from 'react';

import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { Route, Switch, useParams } from 'react-router-dom';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';

import ExplorePage from './ExplorePage';
import NormSetBuilder from './NormSetBuilder';
import NormSetDetail from './NormSetDetail';
import NormSetList from './NormSetList';
import PerceptionModelDetail from './PerceptionModelDetail';
import TrainPerceptionModel from './TrainPerceptionModel';
import TranslationDetail from './TranslationDetail';
import TranslationPage from './TranslationPage';
import { PagePaper } from '../../styles/themeComponents';

/**
 * The id is typed Int! server-side, so a non-numeric path segment is rejected
 * here rather than sent as a query that can only fail.
 */
const useNormSetIdParam = () => {
  const { normSetId } = useParams<{ normSetId: string }>();
  const parsed = Number(normSetId);
  return { id: Number.isInteger(parsed) ? parsed : null, raw: normSetId };
};

const NotANormSet: React.FC<{ raw: string }> = ({ raw }) => (
  <PagePaper>
    <Typography>“{raw}” is not a norm set id.</Typography>
  </PagePaper>
);

const NormSetDetailRoute: React.FC = () => {
  const { id, raw } = useNormSetIdParam();
  return id === null ? (
    <NotANormSet raw={raw} />
  ) : (
    <NormSetDetail normSetId={id} />
  );
};

const TranslationRoute: React.FC = () => {
  const { id, raw } = useNormSetIdParam();
  return id === null ? (
    <NotANormSet raw={raw} />
  ) : (
    <TranslationPage normSetId={id} />
  );
};

/** Both ids are Int! server-side, so a non-numeric segment is rejected here. */
const TranslationDetailRoute: React.FC = () => {
  const { id, raw } = useNormSetIdParam();
  const { translationId } = useParams<{ translationId: string }>();
  const parsed = Number(translationId);

  if (id === null) return <NotANormSet raw={raw} />;
  if (!Number.isInteger(parsed)) {
    return (
      <PagePaper>
        <Typography>“{translationId}” is not a translation id.</Typography>
      </PagePaper>
    );
  }
  // Keyed: without it, routing from one translation to another updates the
  // same element in place, and the unsaved product list from the row just left
  // stays in state — ready to be saved onto the row just opened.
  return (
    <TranslationDetail key={parsed} normSetId={id} translationId={parsed} />
  );
};

/** Int! server-side, so a non-numeric segment is rejected before it is sent. */
const PerceptionModelRoute: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const parsed = Number(jobId);
  return Number.isInteger(parsed) ? (
    <PerceptionModelDetail jobId={parsed} />
  ) : (
    <PagePaper>
      <Typography>“{jobId}” is not a perception model id.</Typography>
    </PagePaper>
  );
};

const PerceptionModels: React.FC = () => {
  const producerId = useSelector((state) => selectWorkspaceProducerId(state));

  return (
    <Switch>
      <Route
        exact
        path={PERCEPTION_MODEL_NEW}
        render={() => <NormSetBuilder producerId={producerId} />}
      />
      <Route exact path={PERCEPTION_MODEL_EXPLORE} component={ExplorePage} />
      <Route
        exact
        path={PERCEPTION_MODEL_TRAIN}
        component={TrainPerceptionModel}
      />
      {/* Two segments, so ":normSetId" cannot swallow it whatever the order,
          but kept with the other literals for the same reason they are. */}
      <Route
        exact
        path={PERCEPTION_MODEL_JOB}
        component={PerceptionModelRoute}
      />
      {/* Before the norm set route, which is only exact by virtue of that
          flag — leaving the order to it would make this path's reachability
          depend on a prop rather than on where it sits. */}
      <Route
        exact
        path={PERCEPTION_MODEL_TRANSLATION_NEW}
        component={TranslationRoute}
      />
      {/* After "new", which would otherwise be read as a translation id. */}
      <Route
        exact
        path={PERCEPTION_MODEL_TRANSLATION}
        component={TranslationDetailRoute}
      />
      {/* After the literal paths: ":normSetId" would otherwise swallow
          "explore" and send it to the not-a-norm-set-id guard. */}
      <Route
        exact
        path={PERCEPTION_MODEL_NORM_SET}
        render={() => <NormSetDetailRoute />}
      />
      <Route exact path={PERCEPTION_MODELS} component={NormSetList} />
    </Switch>
  );
};

export default PerceptionModels;
